import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock } from 'lucide-react';

import UserDetails from './UserDetails';
import PasswordChange from './PasswordChange';
import { useSiteContent } from '../../lib/siteContent';
import { useCompanyProfile, companyDisplayName, companyComposedAddress } from '../../lib/companyProfile';
import { getCurrentUser } from '../../lib/permissions';
import { billingApi } from '../../api';
import { getSubscriptionCache, setSubscriptionCache } from '../../lib/subscription';
import { useTranslation } from '../../lib/i18n';

const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [expiresLabel, setExpiresLabel] = useState<string | null>(getSubscriptionCache()?.expiresLabel ?? null);
  const [planName, setPlanName] = useState<string | null>(getSubscriptionCache()?.plan?.name ?? null);
  const { content } = useSiteContent();
  const { profile: company } = useCompanyProfile();

  useEffect(() => {
    void billingApi.status().then((res) => {
      setSubscriptionCache(res.subscription);
      setExpiresLabel(res.subscription.expiresLabel);
      setPlanName(res.subscription.plan?.name ?? res.planId);
    }).catch(() => {});
  }, []);

  // The profile card should reflect the company details saved in Settings →
  // Company (plus the signed-in admin), falling back to any manually-set profile
  // display. Without this, saved company data never surfaced on the profile page
  // because it lives in a separate store.
  const displayUser = useMemo(() => {
    const me = getCurrentUser();
    const pd = content.profileDisplay;
    const pick = (...vals: (string | undefined | null)[]) =>
      vals.map((v) => (v ?? '').trim()).find((v) => v) ?? '';
    return {
      name: pick(companyDisplayName(company), pd.name, me?.name),
      id: pick(pd.id, me?.id),
      email: pick(company.email, pd.email, me?.email),
      phone: pick(company.phone, company.altPhone, pd.phone, me?.phone),
      address: pick(companyComposedAddress(company), pd.address),
      avatar: pick(company.logoUrl, pd.avatar),
    };
  }, [company, content.profileDisplay]);

  return (
    <div className="space-y-8">
      <UserDetails user={displayUser} />

      {(planName || expiresLabel) ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-5 dark:border-emerald-800/60 dark:bg-emerald-950/35">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">{t('settings.subscription')}</p>
              <p className="mt-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{planName || t('settingsForm.subscriptionPlan')}</p>
            </div>
            {expiresLabel ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200 dark:bg-zinc-800 dark:text-emerald-300 dark:ring-emerald-700/50">
                <CalendarClock className="h-3.5 w-3.5" />
                {expiresLabel}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="rounded-md border border-zinc-200 bg-zinc-50/40 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 pb-4 dark:border-zinc-700">
          <div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('settings.profile')}</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('settings.profileDesc')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowChangePassword(!showChangePassword)}
            className="text-sm font-semibold text-emerald-800 hover:text-emerald-950 dark:text-emerald-400 dark:hover:text-emerald-300"
          >
            {showChangePassword ? t('common.cancel') : t('settingsForm.changePassword')}
          </button>
        </div>

        {showChangePassword && (
          <div className="pt-5">
            <PasswordChange />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSettings;
